import { useState } from "react";
import { Calendar as CalendarIcon, Clock, Mail, Phone, User } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface ScheduleConsultationDialogProps {
  children?: React.ReactNode;
  buttonText?: string;
  buttonVariant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive";
  buttonSize?: "default" | "sm" | "lg" | "icon";
  buttonClassName?: string;
}

export const ScheduleConsultationDialog = ({
  children,
  buttonText = "Schedule a Consultation",
  buttonVariant = "default",
  buttonSize = "lg",
  buttonClassName = "",
}: ScheduleConsultationDialogProps) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [preferredDate, setPreferredDate] = useState<Date>();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    preferredTime: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create detailed message with consultation details
      const consultationMessage = `
Consultation Request
Service: ${formData.service || 'Not specified'}
Preferred Date: ${preferredDate ? format(preferredDate, 'PPP') : 'Not specified'}
Preferred Time: ${formData.preferredTime || 'Not specified'}
Additional Details: ${formData.message || 'None'}
      `.trim();

      console.log('Attempting consultation booking:', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: consultationMessage
      });

      const { data, error } = await supabase.from('leads').insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: null,
        message: consultationMessage,
        status: 'new'
      });

      console.log('Consultation insert result:', { data, error });

      if (error) {
        console.error('Consultation insert error:', error);
        throw error;
      }

      toast({
        title: "Consultation Scheduled!",
        description: "We'll contact you shortly to confirm your appointment.",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        preferredTime: "",
        message: "",
      });
      setPreferredDate(undefined);
      setOpen(false);
    } catch (error: any) {
      console.error('Consultation booking error:', error);
      toast({
        title: "Error",
        description: "Failed to schedule consultation. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button
            variant={buttonVariant}
            size={buttonSize}
            className={buttonClassName}
          >
            {buttonText}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
              <CalendarIcon size={18} className="sm:w-5 sm:h-5 text-accent-foreground" />
            </div>
            <span className="leading-tight">Schedule Your Free Consultation</span>
          </DialogTitle>
          <DialogDescription className="text-sm">
            Let's discuss how we can help grow your business. Fill out the form below and we'll get back to you within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
          <div>
            <label htmlFor="consultation-name" className="block text-sm font-medium mb-2">
              <User className="inline w-4 h-4 mr-1" />
              Your Name *
            </label>
            <Input
              id="consultation-name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="consultation-email" className="block text-sm font-medium mb-2">
                <Mail className="inline w-4 h-4 mr-1" />
                Email *
              </label>
              <Input
                id="consultation-email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <label htmlFor="consultation-phone" className="block text-sm font-medium mb-2">
                <Phone className="inline w-4 h-4 mr-1" />
                Phone *
              </label>
              <Input
                id="consultation-phone"
                type="tel"
                placeholder="+44 123 456 7890"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="consultation-service" className="block text-sm font-medium mb-2">
              Service Interested In
            </label>
            <Select
              value={formData.service}
              onValueChange={(value) => setFormData({ ...formData, service: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="website-marketing">Website Marketing</SelectItem>
                <SelectItem value="mobile-app">Mobile App Services</SelectItem>
                <SelectItem value="ai-marketing">AI for Marketing</SelectItem>
                <SelectItem value="social-media">Social Media Services</SelectItem>
                <SelectItem value="digital-marketing">Digital Marketing</SelectItem>
                <SelectItem value="not-sure">Not Sure Yet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <CalendarIcon className="inline w-4 h-4 mr-1" />
                Preferred Date
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !preferredDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {preferredDate ? format(preferredDate, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={preferredDate}
                    onSelect={setPreferredDate}
                    disabled={(date) =>
                      date < new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label htmlFor="consultation-time" className="block text-sm font-medium mb-2">
                <Clock className="inline w-4 h-4 mr-1" />
                Preferred Time
              </label>
              <Select
                value={formData.preferredTime}
                onValueChange={(value) => setFormData({ ...formData, preferredTime: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="9-10">9:00 AM - 10:00 AM</SelectItem>
                  <SelectItem value="10-11">10:00 AM - 11:00 AM</SelectItem>
                  <SelectItem value="11-12">11:00 AM - 12:00 PM</SelectItem>
                  <SelectItem value="12-13">12:00 PM - 1:00 PM</SelectItem>
                  <SelectItem value="13-14">1:00 PM - 2:00 PM</SelectItem>
                  <SelectItem value="14-15">2:00 PM - 3:00 PM</SelectItem>
                  <SelectItem value="15-16">3:00 PM - 4:00 PM</SelectItem>
                  <SelectItem value="16-17">4:00 PM - 5:00 PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label htmlFor="consultation-message" className="block text-sm font-medium mb-2">
              Additional Details (Optional)
            </label>
            <Textarea
              id="consultation-message"
              rows={3}
              placeholder="Tell us briefly what you'd like to discuss..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 w-full"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1 w-full bg-accent text-accent-foreground hover:bg-accent/90">
              Schedule Consultation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
